import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2 } from "lucide-react";

const EMPTY = { business_name: "", state: "", owner_name: "", owner_phone: "", gstin: "", address: "", email: "" };

export function AddClientDialog({ open, onOpenChange }) {
  const { switchTenant } = useAuth();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (!f.business_name.trim() || !f.state.trim() || !f.owner_name.trim() || f.owner_phone.replace(/\D/g, "").length < 10) {
      toast.error("Fill business name, state, owner name and a 10-digit owner phone");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/super-admin/tenants", f);
      toast.success(`${data.tenant.name} onboarded. Owner ${data.owner.name} can log in with +91 ${data.owner.phone}.`);
      setF(EMPTY);
      onOpenChange(false);
      await switchTenant(data.tenant.id);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="add-client-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-[#F83A84]" /> Onboard a new client</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1 max-h-[65vh] overflow-y-auto">
          <div className="space-y-1.5">
            <Label>Business name *</Label>
            <Input value={f.business_name} onChange={set("business_name")} placeholder="e.g. Sharma Electricals" data-testid="client-business-name" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>State (place of supply) *</Label>
              <Input value={f.state} onChange={set("state")} placeholder="e.g. Bihar" data-testid="client-state" />
            </div>
            <div className="space-y-1.5">
              <Label>GSTIN</Label>
              <Input value={f.gstin} onChange={set("gstin")} placeholder="Optional" data-testid="client-gstin" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Business address</Label>
            <Input value={f.address} onChange={set("address")} placeholder="Optional" data-testid="client-address" />
          </div>
          <div className="border-t border-[#E6E8F0] pt-4 space-y-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-[#6B7189]">Owner login</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Owner name *</Label>
                <Input value={f.owner_name} onChange={set("owner_name")} placeholder="Full name" data-testid="client-owner-name" />
              </div>
              <div className="space-y-1.5">
                <Label>Owner mobile *</Label>
                <Input value={f.owner_phone} onChange={set("owner_phone")} placeholder="10-digit number" data-testid="client-owner-phone" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Owner email</Label>
              <Input value={f.email} onChange={set("email")} placeholder="Optional" data-testid="client-owner-email" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="client-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="client-submit">{saving ? "Creating…" : "Create client"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
